/**
 * Content Lifecycle
 * ---------------------------------------------------------------
 * Canonical state machine for content_assets. Centralizes:
 *  - allowed status transitions
 *  - tactical badge color tokens
 *  - the single write path that flips status AND logs the
 *    transition into content_events (fire-and-forget telemetry,
 *    same style as src/lib/contentTracking.ts)
 *
 * No UI lives here. No mock metrics.
 */

import { supabase } from "@/integrations/supabase/client";

export type ContentStatus =
  | "generated"
  | "queued"
  | "rendering"
  | "rendered"
  | "exported"
  | "published"
  | "archived"
  | "failed";

export const CONTENT_STATUSES: ContentStatus[] = [
  "generated",
  "queued",
  "rendering",
  "rendered",
  "exported",
  "published",
  "archived",
  "failed",
];

/**
 * Tactical badge classes — keyed to the GreyRock AI dark forensic
 * palette. Returned as a className string so the queue stays
 * visually consistent with the rest of the admin console.
 */
export function getStatusColor(status: ContentStatus | string | null): string {
  switch (status) {
    case "generated":
      return "border-cyan/40 text-cyan bg-cyan/10";
    case "queued":
      return "border-cyan/30 text-cyan/80 bg-cyan/5";
    case "rendering":
      return "border-cyan/50 text-cyan bg-cyan/10 animate-pulse";
    case "rendered":
      return "border-emerald-400/50 text-emerald-300 bg-emerald-400/10";
    case "exported":
      return "border-amber-400/50 text-amber-300 bg-amber-400/10";
    case "published":
      return "border-violet-400/50 text-violet-300 bg-violet-400/10";
    case "archived":
      return "border-muted-foreground/40 text-muted-foreground bg-muted-foreground/10";
    case "failed":
      return "border-red-500/50 text-red-400 bg-red-500/10";
    default:
      return "border-muted-foreground/40 text-muted-foreground bg-muted-foreground/10";
  }
}

// ---------- Permission helpers ----------

const ACTIVE_STATUSES: ContentStatus[] = [
  "generated",
  "queued",
  "rendering",
  "rendered",
  "exported",
  "published",
  "failed",
];

export function canRender(status: ContentStatus | string | null): boolean {
  return status === "generated" || status === "queued";
}

export function canExport(status: ContentStatus | string | null): boolean {
  return status === "rendered";
}

export function canArchive(status: ContentStatus | string | null): boolean {
  return ACTIVE_STATUSES.includes(status as ContentStatus);
}

// ---------- Lifecycle write path ----------

/**
 * Flip a content_assets row to a new canonical status and log
 * the transition into content_events. Fire-and-forget telemetry —
 * the status update is awaited (caller wants confirmation) but
 * the event log is best-effort and never throws upstream.
 */
export async function updateContentStatus(
  assetId: string,
  status: ContentStatus,
  opts: { from?: ContentStatus | string | null; metadata?: Record<string, unknown> } = {}
): Promise<{ success: boolean; error?: string }> {
  if (!assetId) return { success: false, error: "Missing assetId" };

  // Capture current status for the event payload if not supplied.
  let from = opts.from ?? null;
  if (from == null) {
    const { data } = await supabase
      .from("content_assets" as any)
      .select("status")
      .eq("id", assetId)
      .maybeSingle();
    from = (data as any)?.status ?? null;
  }

  const { error } = await supabase
    .from("content_assets" as any)
    .update({ status, updated_at: new Date().toISOString() })
    .eq("id", assetId);

  if (error) {
    // Log the failure as a lifecycle event too — best effort.
    void logLifecycleEvent(assetId, {
      event_type: "status_change_failed",
      from,
      to: status,
      error: error.message,
      ...(opts.metadata ?? {}),
    });
    return { success: false, error: error.message };
  }

  void logLifecycleEvent(assetId, {
    event_type: "status_changed",
    from,
    to: status,
    ...(opts.metadata ?? {}),
  });

  return { success: true };
}

/** Fire-and-forget event insert. Mirrors contentTracking.ts style. */
function logLifecycleEvent(assetId: string, payload: Record<string, unknown>): void {
  void (async () => {
    try {
      await supabase.from("content_events" as any).insert({
        content_asset_id: assetId,
        event_type: "status_changed",
        metadata: payload as any,
      });
    } catch (e) {
      console.error("[contentLifecycle] event log failed:", e);
    }
  })();
}
